/**
 * Service pour la gestion des collections vocales
 */

import { authenticatedFetch } from './auth';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// Configuration pour les requêtes
const defaultConfig = {
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

// Utilitaire pour gérer les erreurs HTTP
const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.error || 'Une erreur est survenue');
  }
  return response.json();
};

// Utilitaire pour créer une requête avec retry
const fetchWithRetry = async (url, config = {}, maxRetries = 1) => {
  let lastError;

  for (let i = 0; i <= maxRetries; i++) {
    try {
      const response = await authenticatedFetch(url, { ...defaultConfig, ...config });
      return await handleResponse(response);
    } catch (error) {
      lastError = error;

      // Ne pas retry si c'est une erreur client (4xx) ou connection refused
      if (error.message.includes('400') || error.message.includes('404') || error.message.includes('Failed to fetch')) {
        throw error;
      }

      // Attendre avant le prochain essai (réduit à 500ms)
      if (i < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 500 * (i + 1)));
      }
    }
  }

  throw lastError;
};

// Cache en mémoire pour les langues (éviter appels répétés)
let cachedLanguages = null;
let languagesCacheTime = 0;
const CACHE_DURATION = 60000; // 1 minute

// Mock data removed


// LocalStorage helpers removed




// Service pour les collections
export const collectionsService = {
  /**
   * Récupérer toutes les collections
   * @returns {Promise<Array>} Liste des collections
   */
  async getAll() {
    return await fetchWithRetry(`${API_BASE_URL}/collections`);
  },

  /**
   * Récupérer une collection par ID
   * @param {number} id - ID de la collection
   * @returns {Promise<Object>} Collection trouvée
   */
  async getById(id) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${id}`);
  },

  /**
   * Créer une nouvelle collection
   * @param {Object} collectionData - Données de la collection
   * @returns {Promise<Object>} Collection créée
   */
  async create(collectionData) {
    return await fetchWithRetry(`${API_BASE_URL}/collections`, {
      method: 'POST',
      body: JSON.stringify(collectionData)
    });
  },

  /**
   * Mettre à jour une collection
   * @param {number} id - ID de la collection
   * @param {Object} updates - Modifications à apporter
   * @returns {Promise<Object>} Collection mise à jour
   */
  async update(id, updates) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Supprimer une collection
   * @param {number} id - ID de la collection
   * @returns {Promise<Object>} Résultat de la suppression
   */
  async delete(id) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${id}`, {
      method: 'DELETE'
    });
  },

  /**
   * Lister les collections supprimées (corbeille)
   */
  async getDeleted() {
    return await fetchWithRetry(`${API_BASE_URL}/collections/deleted`);
  },

  /**
   * Restaurer une collection supprimée
   * @param {number} id - ID de la collection
   */
  async restore(id) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${id}/restore`, {
      method: 'PUT'
    });
  },

  /**
   * Récupérer les collections par catégorie
   * @param {string} category - Catégorie des collections
   * @returns {Promise<Array>} Collections de la catégorie
   */
  async getByCategory(category) {
    return await fetchWithRetry(`${API_BASE_URL}/collections?category=${category}`);
  },

  // ----- Partage aux contributeurs (même fonctionnement que la transcription) -----

  /**
   * Liens de partage d'une collection (complet, sections, sélections) avec avancement
   */
  async getShares(collectionId) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${collectionId}/shares`);
  },

  /**
   * Créer (ou retrouver) un lien : {} complet, { from, to } section, { phraseIndices } sélection.
   * Options : permission ('edit' | 'read'), showTranslations, description.
   */
  async createShare(collectionId, body = {}) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${collectionId}/shares`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  /**
   * Révoquer un lien de partage (les enregistrements déjà faits sont conservés)
   */
  async revokeShare(collectionId, shareId) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${collectionId}/shares/${shareId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Masquer (hide) ou réafficher (show) des phrases dans les liens contributeurs
   */
  async bulkPhraseAction(collectionId, indices, action) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${collectionId}/phrases/bulk-action`, {
      method: 'POST',
      body: JSON.stringify({ indices, action })
    });
  },

  /**
   * Remplacer le texte d'une phrase (propriétaire), ex. « Rétablir ce texte » depuis l'historique.
   * field = 'text' | 'translation' | 'translation_en'
   */
  async setPhraseText(collectionId, index, field, text, comment) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${collectionId}/phrases/${index}/text`, {
      method: 'POST',
      body: JSON.stringify({ field, text, comment })
    });
  }
};

// Service pour les langues
export const languagesService = {
  /**
   * Récupérer toutes les langues disponibles
   * @returns {Promise<Array>} Liste des langues
   */
  async getAll() {
    // Utiliser le cache si disponible et récent
    const now = Date.now();
    if (cachedLanguages && (now - languagesCacheTime) < CACHE_DURATION) {
      return cachedLanguages;
    }

    // Utiliser fetch standard car les langues sont publiques (pas besoin d'auth)
    try {
      const response = await fetch(`${API_BASE_URL}/languages`, {
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Erreur chargement langues');
      const result = await response.json();
      cachedLanguages = result;
      languagesCacheTime = now;
      return result;
    } catch (error) {
      console.error('Erreur langues:', error);
      // Fallback avec langues par défaut
      return {
        success: true, data: [
          { code: 'wo', name: 'Wolof', flag: '🇸🇳' },
          { code: 'fr', name: 'Français', flag: '🇫🇷' },
          { code: 'en', name: 'English', flag: '🇺🇸' }
        ]
      };
    }
  },

  /**
   * Récupérer une langue par son code
   * @param {string} code - Code de la langue (ex: 'fr', 'en')
   * @returns {Promise<Object>} Langue trouvée
   */
  async getByCode(code) {
    return await fetchWithRetry(`${API_BASE_URL}/languages/${code}`);
  },

  /**
   * Ajouter une nouvelle langue
   * @param {Object} languageData - Données de la langue
   * @returns {Promise<Object>} Langue créée
   */
  async create(languageData) {
    console.log('📤 [API] Creating language:', languageData);
    const result = await fetchWithRetry(`${API_BASE_URL}/languages`, {
      method: 'POST',
      body: JSON.stringify(languageData)
    });
    // Invalider le cache
    cachedLanguages = null;
    return result;
  },

  /**
   * Modifier une langue existante
   * @param {string} code - Code de la langue
   * @param {Object} languageData - Données à mettre à jour
   * @returns {Promise<Object>} Langue modifiée
   */
  async update(code, languageData) {
    console.log('📤 [API] Updating language:', code, languageData);
    const result = await fetchWithRetry(`${API_BASE_URL}/languages/${code}`, {
      method: 'PUT',
      body: JSON.stringify(languageData)
    });
    // Invalider le cache
    cachedLanguages = null;
    return result;
  }
};

// Service pour les enregistrements audio
export const audioService = {
  /**
   * Sauvegarder un enregistrement audio
   * @param {Blob} audioBlob - Données audio
   * @param {Object} metadata - Métadonnées de l'enregistrement
   * @returns {Promise<Object>} Résultat de la sauvegarde
   */
  async saveRecording(audioBlob, metadata) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    formData.append('metadata', JSON.stringify(metadata));

    return await fetchWithRetry(`${API_BASE_URL}/recordings`, {
      method: 'POST',
      body: formData,
      headers: {} // Pas de Content-Type pour FormData
    });
  },

  /**
   * Récupérer les enregistrements d'une collection
   * @param {number} collectionId - ID de la collection
   * @returns {Promise<Array>} Liste des enregistrements
   */
  async getRecordings(collectionId) {
    return await fetchWithRetry(`${API_BASE_URL}/collections/${collectionId}/recordings`);
  }
};

// Cache simple pour les données fréquemment utilisées
class SimpleCache {
  constructor(ttl = 5 * 60 * 1000) { // 5 minutes par défaut
    this.cache = new Map();
    this.ttl = ttl;
  }

  set(key, value) {
    const expiry = Date.now() + this.ttl;
    this.cache.set(key, { value, expiry });
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;

    if (Date.now() > item.expiry) {
      this.cache.delete(key);
      return null;
    }

    return item.value;
  }

  clear() {
    this.cache.clear();
  }
}

export const cache = new SimpleCache();

// ==================== VERSION 1.0.1 - SERVICE COLLECTIONS DE TRADUCTIONS ====================

// Service pour les collections de traductions (texte-texte)
export const translationCollectionsService = {
  /**
   * Récupérer toutes les collections de traductions
   * @returns {Promise<Array>} Liste des collections
   */
  async getAll() {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections`);
  },

  /**
   * Récupérer une collection par ID
   * @param {number} id - ID de la collection
   * @returns {Promise<Object>} Collection trouvée
   */
  async getById(id) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${id}`);
  },

  /**
   * Créer une nouvelle collection
   * @param {Object} data - Données de la collection
   * @returns {Promise<Object>} Collection créée
   */
  async create(data) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /**
   * Mettre à jour une collection
   * @param {number} id - ID de la collection
   * @param {Object} updates - Modifications
   * @returns {Promise<Object>} Collection mise à jour
   */
  async update(id, updates) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Supprimer une collection
   * @param {number} id - ID de la collection
   * @returns {Promise<Object>} Résultat
   */
  async delete(id) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${id}`, {
      method: 'DELETE'
    });
  },

  /**
   * Récupérer les paires d'une collection
   * @param {number} collectionId - ID de la collection
   * @returns {Promise<Object>} Collection et paires
   */
  async getPairs(collectionId) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${collectionId}/pairs`);
  },

  /**
   * Ajouter une paire de traduction
   * @param {number} collectionId - ID de la collection
   * @param {Object} pair - Paire source/target
   * @returns {Promise<Object>} Paire créée
   */
  async addPair(collectionId, pair) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${collectionId}/pairs`, {
      method: 'POST',
      body: JSON.stringify(pair)
    });
  },

  /**
   * Import en masse de paires (depuis un CSV/Excel/JSON)
   * @param {number} collectionId
   * @param {Array<{source_text,target_text,target_text_en}>} pairs
   */
  async addPairsBulk(collectionId, pairs) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${collectionId}/pairs/bulk`, {
      method: 'POST',
      body: JSON.stringify({ pairs })
    });
  },

  /**
   * Créer une collection vocale à partir d'une collection de traduction
   * @param {number} id - ID de la collection de traduction
   * @returns {Promise<Object>} { success, data: collection }
   */
  async createCollectionFromTranslation(id) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${id}/create-collection`, {
      method: 'POST',
      body: JSON.stringify({})
    });
  },

  /**
   * Mettre à jour une paire
   * @param {number} pairId - ID de la paire
   * @param {Object} updates - Modifications
   * @returns {Promise<Object>} Paire mise à jour
   */
  async updatePair(pairId, updates) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-pairs/${pairId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Supprimer une paire
   * @param {number} pairId - ID de la paire
   * @returns {Promise<Object>} Résultat
   */
  async deletePair(pairId) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-pairs/${pairId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Exporter en JSONL (format HuggingFace)
   * @param {number} collectionId - ID de la collection
   * @param {boolean} bidirectional - Export bidirectionnel
   * @returns {Promise<Blob>} Fichier JSONL
   */
  async exportJSONL(collectionId, bidirectional = false) {
    const endpoint = bidirectional
      ? `${API_BASE_URL}/translation-collections/${collectionId}/export/jsonl-bidirectional`
      : `${API_BASE_URL}/translation-collections/${collectionId}/export/jsonl`;

    const response = await authenticatedFetch(endpoint);
    if (!response.ok) {
      throw new Error('Erreur lors de l\'export');
    }
    return await response.blob();
  },

  /**
   * Récupérer toutes les paires de l'utilisateur
   * @returns {Promise<Array>} Liste des paires
   */
  async getAllPairs() {
    return await fetchWithRetry(`${API_BASE_URL}/translation-pairs/all`);
  },

  /**
   * Créer un lien de partage pour une collection
   * @param {number} collectionId - ID de la collection
   * @returns {Promise<Object>} Infos de partage (shareId, url)
   */
  async share(collectionId) {
    return await fetchWithRetry(`${API_BASE_URL}/shared-translation-collections`, {
      method: 'POST',
      body: JSON.stringify({ collectionId })
    });
  },

  /**
   * Liens de partage d'une collection (complet, sections, sélections) avec avancement
   */
  async getShares(collectionId) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${collectionId}/shares`);
  },

  /**
   * Créer (ou retrouver) un lien : {} complet, { from, to } section, { pairIds } sélection
   */
  async createShare(collectionId, body = {}) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${collectionId}/shares`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  },

  /**
   * Révoquer un lien de partage (les contributions déjà faites sont conservées)
   */
  async revokeShare(collectionId, shareId) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${collectionId}/shares/${shareId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Masquer (hide) ou réafficher (show) des phrases dans les liens contributeurs
   */
  async bulkAction(collectionId, ids, action) {
    return await fetchWithRetry(`${API_BASE_URL}/translation-collections/${collectionId}/pairs/bulk-action`, {
      method: 'POST',
      body: JSON.stringify({ ids, action })
    });
  },

  /**
   * Récupérer mes partages de collections de traduction
   * @returns {Promise<Array>} Liste des partages
   */
  async getMyShares() {
    return await fetchWithRetry(`${API_BASE_URL}/my-shared-translation-collections`);
  },

  /**
   * Supprimer un partage
   * @param {string} shareId - ID du partage
   * @returns {Promise<Object>} Résultat
   */
  async deleteShare(shareId) {
    return await fetchWithRetry(`${API_BASE_URL}/shared-translation-collections/${shareId}`, {
      method: 'DELETE'
    });
  }
};

/**
 * Service pour les collections de traduction partagées (accès public)
 */
export const sharedTranslationService = {
  /**
   * Récupérer une collection partagée par son ID de partage
   * @param {string} shareId - ID du partage
   * @returns {Promise<Object>} Collection partagée avec paires
   */
  async getByShareId(shareId) {
    const response = await fetch(`${API_BASE_URL}/shared-translation-collections/${shareId}`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Collection non trouvée');
    }
    return await response.json();
  },

  /**
   * Ajouter une paire à une collection partagée (public)
   * @param {string} shareId - ID du partage
   * @param {Object} pair - Paire à ajouter
   * @returns {Promise<Object>} Paire créée
   */
  async addPair(shareId, pair) {
    const response = await fetch(`${API_BASE_URL}/shared-translation-collections/${shareId}/pairs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pair)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erreur lors de l\'ajout');
    }
    return await response.json();
  },

  /**
   * Modifier une paire partagée
   * @param {string} shareId - ID du partage
   * @param {string} pairId - ID de la paire
   * @param {Object} updates - Modifications
   */
  async updatePair(shareId, pairId, updates) {
    const response = await fetch(`${API_BASE_URL}/shared-translation-collections/${shareId}/pairs/${pairId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erreur lors de la modification');
    }
    return await response.json();
  },

  /**
   * Revue d'une proposition : action = 'propose' | 'validate' | 'reject' | 'modify' | 'edit_targets'
   */
  async reviewPair(shareId, pairId, payload) {
    const response = await fetch(`${API_BASE_URL}/shared-translation-collections/${shareId}/pairs/${pairId}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Erreur lors de la revue');
    }
    return await response.json();
  },

  /**
   * Supprimer une paire partagée
   * @param {string} shareId - ID du partage
   * @param {string} pairId - ID de la paire
   */
  async deletePair(shareId, pairId) {
    const response = await fetch(`${API_BASE_URL}/shared-translation-collections/${shareId}/pairs/${pairId}`, {
      method: 'DELETE'
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erreur lors de la suppression');
    }
    return await response.json();
  }
};

/**
 * Service pour les opérations d'administration/récupération
 */
export const adminService = {
  /**
   * Récupérer toutes les collections orphelines et les assigner à l'utilisateur
   * @returns {Promise<Object>} Résultat de la récupération
   */
  async fixAllOrphans() {
    return fetchWithRetry(`${API_BASE_URL}/orphans/fix`, {
      method: 'GET'
    });
  },

  /**
   * Réparer une collection partagée spécifique
   * @param {string} sharedId - ID de la collection partagée
   * @returns {Promise<Object>} Résultat de la réparation
   */
  async fixSharedCollection(sharedId) {
    return fetchWithRetry(`${API_BASE_URL}/admin/fix-shared/${sharedId}`, {
      method: 'GET'
    });
  }
};
